import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { SECTIONS, grandTotals, withCumulative } from "../services/fixedFeeCalculator";

export const money = (value) => `PKR ${Math.round(Number(value || 0)).toLocaleString("en-PK")}`;
const signed = (value) => (Math.round(value) < 0 ? `(${money(Math.abs(value))})` : money(value));
export const monthLabel = (period) => {
  const [year, month] = String(period || "").split("-").map(Number);
  if (!year || !month) return period || "";
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
};

const pct = (rate) => Math.round(Number(rate || 0) * 1000) / 10;

export function exportStatementsExcel(statements, clientName) {
  const rows = withCumulative(statements);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([
    ["Month", "Fixed fee", "Tax charged", "Total invoiced", "WHT withheld by client", "Sales tax withheld by client", "Cash received", "Salaries", "Expenses", "Taxes paid", "Service charge", "Total paid out", "Saved", "Saved to date"],
    ...rows.map((row) => [monthLabel(row.period), row.totals.fee, row.totals.taxCollected, row.totals.invoiced, row.totals.whtWithheld, row.totals.taxWithheld, row.totals.received, row.totals.salaries, row.totals.expenses, row.totals.taxesPaid, row.totals.serviceCharge, row.totals.paidOut, row.totals.saved, row.cumulativeSaved])
  ]), "Summary");
  const detail = [["Month", "Section", "Description", "Amount", "Note"]];
  rows.forEach((row) => {
    detail.push([monthLabel(row.period), "Fixed fee received", "Fixed monthly fee", row.totals.fee, ""]);
    detail.push([monthLabel(row.period), "Tax charged", `Sales tax @ ${pct(row.taxRate)}%`, row.totals.taxCollected, ""]);
    detail.push([monthLabel(row.period), "Withheld by client", `Income tax WHT @ ${pct(row.whtRate)}% of invoice`, -row.totals.whtWithheld, ""]);
    detail.push([monthLabel(row.period), "Withheld by client", `Sales tax withheld @ ${pct(row.taxWithholdingRate)}%`, -row.totals.taxWithheld, ""]);
    row.lines.forEach((line) => detail.push([monthLabel(row.period), SECTIONS[line.section], line.description, line.amount, line.note || ""]));
    detail.push([monthLabel(row.period), "Service charge", "Fixed service charge", row.totals.serviceCharge, ""]);
  });
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(detail), "Detail");
  saveAs(new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })], { type: "application/octet-stream" }), `${clientName.replace(/\s+/g, "_")}_Monthly_Statement.xlsx`);
}

// Read-only statement used by the editor's print view and by the public link.
export default function FixedFeeReport({ clientName, statements, footer }) {
  const rows = withCumulative(statements);
  const totals = grandTotals(statements);
  const monthsNewestFirst = [...rows].reverse();

  return (
    <article className="pnl-report" style={{ marginTop: 16 }}>
      <div className="pnl-letterhead">
        <div className="pnl-letterhead-brand">
          <img src="/alm-logo.png" alt="ALM logo" />
          <div>
            <h2 className="pnl-letterhead-title">ALM Outsourcing Services (Pvt) Ltd.</h2>
            <p className="pnl-letterhead-subtitle">Monthly Fixed-Fee Statement — {clientName}</p>
          </div>
        </div>
        <div className="pnl-letterhead-meta">
          <strong>{rows.length ? `${monthLabel(rows[0].period)} to ${monthLabel(rows[rows.length - 1].period)}` : "No months yet"}</strong><br />
          {rows.length} month{rows.length === 1 ? "" : "s"}<br />
          Printed {new Date().toLocaleDateString("en-GB")}
        </div>
      </div>

      <h3>Summary</h3>
      <table className="data-table">
        <thead><tr><th>Month</th><th>Invoiced</th><th>Withheld by client</th><th>Cash received</th><th>Salaries</th><th>Expenses</th><th>Taxes paid</th><th>Service charge</th><th>Saved</th><th>Saved to date</th></tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.period}>
              <td>{monthLabel(row.period)}</td><td>{money(row.totals.invoiced)}</td><td>{money(row.totals.withheldTotal)}</td><td>{money(row.totals.received)}</td>
              <td>{money(row.totals.salaries)}</td><td>{money(row.totals.expenses)}</td><td>{money(row.totals.taxesPaid)}</td><td>{money(row.totals.serviceCharge)}</td>
              <td><strong>{signed(row.totals.saved)}</strong></td><td>{signed(row.cumulativeSaved)}</td>
            </tr>
          ))}
          {rows.length > 0 && (
            <tr style={{ fontWeight: 700 }}>
              <td>Total</td><td>{money(totals.invoiced)}</td><td>{money(totals.withheldTotal)}</td><td>{money(totals.received)}</td><td>{money(totals.salaries)}</td><td>{money(totals.expenses)}</td>
              <td>{money(totals.taxesPaid)}</td><td>{money(totals.serviceCharge)}</td><td>{signed(totals.saved)}</td><td />
            </tr>
          )}
          {!rows.length && <tr><td colSpan={10}>Nothing has been published yet.</td></tr>}
        </tbody>
      </table>

      {monthsNewestFirst.map((row) => (
        <section key={row.period} style={{ marginTop: 22, breakInside: "avoid" }}>
          <h3>{monthLabel(row.period)}</h3>
          <table className="data-table">
            <thead><tr><th>Item</th><th>Note</th><th>Amount</th></tr></thead>
            <tbody>
              <tr style={{ fontWeight: 700 }}><td>Fixed monthly fee</td><td /><td>{money(row.totals.fee)}</td></tr>
              <tr><td>Sales tax charged @ {pct(row.taxRate)}%</td><td /><td>{money(row.totals.taxCollected)}</td></tr>
              <tr style={{ fontWeight: 700 }}><td>Total invoiced</td><td /><td>{money(row.totals.invoiced)}</td></tr>
              <tr><td>Less: income tax withheld by client @ {pct(row.whtRate)}% of invoice</td><td /><td>({money(row.totals.whtWithheld)})</td></tr>
              <tr><td>Less: sales tax withheld by client @ {pct(row.taxWithholdingRate)}%</td><td /><td>({money(row.totals.taxWithheld)})</td></tr>
              <tr style={{ fontWeight: 700 }}><td>Cash received</td><td /><td>{money(row.totals.received)}</td></tr>
              {Object.entries(SECTIONS).map(([key, label]) => {
                const lines = row.lines.filter((line) => line.section === key);
                if (!lines.length) return null;
                return [
                  <tr key={`${key}-h`}><td colSpan={3} style={{ fontWeight: 700, background: "var(--surface)" }}>{label}</td></tr>,
                  ...lines.map((line, index) => <tr key={`${key}-${index}`}><td>{line.description}</td><td>{line.note}</td><td>{money(line.amount)}</td></tr>)
                ];
              })}
              <tr><td colSpan={3} style={{ fontWeight: 700, background: "var(--surface)" }}>Service charge</td></tr>
              <tr><td>Fixed service charge</td><td /><td>{money(row.totals.serviceCharge)}</td></tr>
              <tr style={{ fontWeight: 700 }}><td>Total paid out</td><td /><td>{money(row.totals.paidOut)}</td></tr>
              <tr style={{ fontWeight: 700 }}><td>Saved this month</td><td>{row.totals.savedPercentOfFee}% of the fixed fee</td><td>{signed(row.totals.saved)}</td></tr>
            </tbody>
          </table>
          {row.notes && <p className="page-copy">{row.notes}</p>}
        </section>
      ))}
      {footer}
    </article>
  );
}
